// src/pages/DepartamentosResidentes.js
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { FaUsers, FaUserCheck, FaUserTimes, FaChevronLeft, FaClipboardList, FaSearch, FaEnvelope, FaWallet, FaDownload, FaTint, FaIdCard } from 'react-icons/fa';
import './DepartamentosDetalle.css';
import { useAuth } from '../context/AuthContext';
import { db } from '../firebase';
import { collection, doc, getDoc, onSnapshot, orderBy, query, getDocs, collectionGroup, where } from 'firebase/firestore';
import ExcelJS from 'exceljs';
import EnviarCorreoModal from '../components/EnviarCorreoModal';
import HistorialBoletinesPanel from '../components/HistorialBoletinesPanel';
import { calcularEstadoMoroso, useConfiguracionFinanciera, esCasaCaseta, esCasaMorosa } from '../utils/morosoUtils';
import { calcularAdeudoVisibleDesdeObjeto } from '../utils/calcularAdeudoVisible';
import ResidentProfileModal from '../components/ResidentProfileModal';
import ModalEdicionSaldos from '../components/ModalEdicionSaldos';
import ModalImportarTagsRFID from '../components/ModalImportarTagsRFID';
import Swal from 'sweetalert2';
import { exportarSaldosCompletosExcel } from '../utils/saldosExcelExport';

const PET_POSITIVE_RESPONSES = new Set(['si', 'sí', 'true', '1', 'con mascotas', 'mascotas', 'yes']);
const PET_NEGATIVE_RESPONSES = new Set(['no', 'false', '0', 'sin mascotas', 'sin mascota', 'sin', 'ninguna', 'ninguno', 'na', 'n/a']);

const toNonNegativeInteger = (value) => {
    if (value === null || value === undefined) {
        return 0;
    }
    if (typeof value === 'number') {
        return Number.isFinite(value) && value >= 0 ? value : 0;
    }
    if (typeof value === 'boolean') {
        return value ? 1 : 0;
    }
    if (typeof value === 'string') {
        const cleaned = value.replace(/[^0-9.,-]/g, '').replace(/,/g, '.').trim();
        if (!cleaned) {
            return 0;
        }
        const parsed = Number(cleaned);
        return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
    }
    const numeric = Number(value);
    return Number.isFinite(numeric) && numeric >= 0 ? numeric : 0;
};

const buildMascotasLabel = (value) => {
    if (value === null || value === undefined) {
        return null;
    }
    if (typeof value === 'boolean') {
        return value ? 'Mascotas' : 'Sin mascotas';
    }
    if (typeof value === 'number') {
        if (!Number.isFinite(value)) {
            return null;
        }
        if (value === 0) {
            return 'Sin mascotas';
        }
        return `${value} mascota${value === 1 ? '' : 's'}`;
    }
    const trimmed = String(value).trim();
    if (!trimmed) {
        return null;
    }
    const normalized = trimmed.toLowerCase();
    if (PET_POSITIVE_RESPONSES.has(normalized)) {
        return 'Mascotas';
    }
    if (PET_NEGATIVE_RESPONSES.has(normalized)) {
        return 'Sin mascotas';
    }
    return trimmed.length <= 32 ? trimmed : `${trimmed.slice(0, 29)}...`;
};

const MAX_RESULTS = 12;

const DepartamentosResidentes = () => {
    const location = useLocation();
    const [searchTerm, setSearchTerm] = useState(() => {
        return location.state?.search || '';
    });
    const [residents, setResidents] = useState([]);
    const [isLoadingData, setIsLoadingData] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [showCorreoModal, setShowCorreoModal] = useState(false);
    const [showCorreoHistory, setShowCorreoHistory] = useState(false);
    const [selectedResidentForProfile, setSelectedResidentForProfile] = useState(null);
    const { fraccionamientoId, userData } = useAuth();
    const navigate = useNavigate();

    // Estados para el modal de Edición de Saldos
    const [showSaldosModal, setShowSaldosModal] = useState(false);
    const [saldosCasaData, setSaldosCasaData] = useState(null);

    // Estado para el modal de Importación Masiva de Tags RFID
    const [showImportarTagsModal, setShowImportarTagsModal] = useState(false);

    const [mapAguaPendiente, setMapAguaPendiente] = useState({});
    const [mapMultasPendientes, setMapMultasPendientes] = useState({});
    const [mapExtrasPendientes, setMapExtrasPendientes] = useState({});

    useEffect(() => {
        if (!fraccionamientoId) return;
        const consumosAguaRef = collection(db, 'fraccionamientos', fraccionamientoId, 'consumosAguaPotable');
        const unsubscribe = onSnapshot(consumosAguaRef, (snapshot) => {
            const mapAdeudo = {};
            snapshot.docs.forEach(docAgua => {
                const dataAgua = docAgua.data();
                const desglose = dataAgua.desgloseCasas || [];
                desglose.forEach(item => {
                    const est = String(item.estadoPago || item.estado || '').toUpperCase();
                    const isPendiente = item.pagado === false || (item.pagado !== true && est !== 'PAGADO');
                    if (isPendiente) {
                        const cId = String(item.casaId || '').trim().toUpperCase();
                        const cNum = String(item.casaNumero || '').trim().toUpperCase();
                        const monto = Number(item.cargoTotal || item.montoPendiente || item.monto || 0);
                        if (cId) {
                            mapAdeudo[cId] = (mapAdeudo[cId] || 0) + monto;
                        }
                        if (cNum) {
                            mapAdeudo[cNum] = (mapAdeudo[cNum] || 0) + monto;
                        }
                    }
                });
            });
            setMapAguaPendiente(mapAdeudo);
        }, (err) => {
            console.warn('DepartamentosResidentes: error escuchando consumosAguaPotable', err);
        });
        return () => unsubscribe();
    }, [fraccionamientoId]);

    // Escuchar todas las multas administrativas no pagadas
    useEffect(() => {
        if (!fraccionamientoId) {
            setMapMultasPendientes({});
            return;
        }
        const q = query(
            collection(db, 'fraccionamientos', fraccionamientoId, 'multasAdministrativas')
        );
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const mapMultas = {};
            snapshot.docs.forEach(docMulta => {
                const data = docMulta.data() || {};
                if (!data.pagada) {
                    const casaId = String(data.casaId || '').trim().toUpperCase();
                    const casaNum = String(data.casaNumero || data.numeroCasa || data.numero || '').trim().toUpperCase();
                    const monto = Number(data.monto || 0);
                    if (casaId) {
                        mapMultas[casaId] = (mapMultas[casaId] || 0) + monto;
                    }
                    if (casaNum && casaNum !== casaId) {
                        mapMultas[casaNum] = (mapMultas[casaNum] || 0) + monto;
                    }
                }
            });
            setMapMultasPendientes(mapMultas);
        }, (err) => {
            console.warn('DepartamentosResidentes: error escuchando multasAdministrativas', err);
        });
        return () => unsubscribe();
    }, [fraccionamientoId]);

    // Escuchar todos los meses pendientes (cuotas extraordinarias)
    useEffect(() => {
        if (!fraccionamientoId) {
            setMapExtrasPendientes({});
            return;
        }
        const q = query(
            collectionGroup(db, 'mesesPendientes'),
            where('fraccionamientoId', '==', String(fraccionamientoId))
        );
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const mapExtras = {};
            const mapAguaFromMeses = {};
            snapshot.docs.forEach(docMes => {
                const data = docMes.data() || {};
                const mesId = docMes.id;
                const est = String(data.estado || '').toLowerCase();
                const esPagada = est === 'pagado_completo' || est === 'pagado_totalmente' || est === 'pagado';
                const esExtraordinaria = mesId.startsWith('EXT-') || data.tipo === 'cuota_extraordinaria';
                const esAguaDoc = mesId.startsWith('AGUA-') || data.tipo === 'adeudo_agua';

                if (!esPagada && esExtraordinaria) {
                    const casaId = String(data.casaId || docMes.ref.parent?.parent?.id || '').trim().toUpperCase();
                    const monto = Number(data.montoPendiente ?? data.monto ?? 0);
                    if (casaId) {
                        mapExtras[casaId] = (mapExtras[casaId] || 0) + monto;
                    }
                }

                if (!esPagada && esAguaDoc) {
                    const casaId = String(data.casaId || docMes.ref.parent?.parent?.id || '').trim().toUpperCase();
                    const casaNum = String(data.casaNumero || '').trim().toUpperCase();
                    const monto = Number(data.montoPendiente ?? data.monto ?? 0);
                    if (casaId) mapAguaFromMeses[casaId] = (mapAguaFromMeses[casaId] || 0) + monto;
                    if (casaNum) mapAguaFromMeses[casaNum] = (mapAguaFromMeses[casaNum] || 0) + monto;
                }
            });
            setMapExtrasPendientes(mapExtras);
            if (Object.keys(mapAguaFromMeses).length > 0) {
                setMapAguaPendiente(prev => ({
                    ...prev,
                    ...mapAguaFromMeses
                }));
            }
        }, (err) => {
            console.warn('DepartamentosResidentes: error escuchando mesesPendientes collectionGroup', err);
        });
        return () => unsubscribe();
    }, [fraccionamientoId]);

    // Obtener configuración financiera para cálculo de morosidad
    const { cuotaMensual, mesesPermitidos, loading: configLoading } = useConfiguracionFinanciera(fraccionamientoId);

    useEffect(() => {
        if (!fraccionamientoId) {
            setResidents([]);
            setIsLoadingData(false);
            setLoadError('');
            return;
        }

        setIsLoadingData(true);
        setLoadError('');

        const casasRef = collection(db, 'fraccionamientos', fraccionamientoId, 'casas');
        const casasQuery = query(casasRef, orderBy('numero'));
        const unsubscribe = onSnapshot(casasQuery, (snapshot) => {
            const nuevosResidentes = snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }));
            nuevosResidentes.sort((a, b) => {
                const numA = String(a.numero || a.numeroCasa || a.lote || '').trim();
                const numB = String(b.numero || b.numeroCasa || b.lote || '').trim();
                return numA.localeCompare(numB, undefined, { numeric: true, sensitivity: 'base' });
            });
            setResidents(nuevosResidentes);
            setIsLoadingData(false);
        }, (error) => {
            console.error('DepartamentosResidentes: error al cargar residentes', error);
            setResidents([]);
            setIsLoadingData(false);
            setLoadError('No se pudo cargar el padron de residentes.');
        });

        return () => unsubscribe();
    }, [fraccionamientoId]);

    const pickFirstString = useCallback((values) => {
        for (const value of values) {
            if (typeof value === 'string') {
                const trimmed = value.trim();
                if (trimmed.length > 0) {
                    return trimmed;
                }
            }
        }
        return '';
    }, []);

    const isCasetaProfile = useCallback((resident) => {
        return esCasaCaseta(resident);
    }, []);

    const handleOpenEditSaldosModal = async (resident) => {
        if (!resident || !resident.id || !fraccionamientoId) return;
        
        try {
            const { doc, getDoc } = await import('firebase/firestore');
            const casaRef = doc(db, 'fraccionamientos', fraccionamientoId, 'casas', resident.id);
            const casaSnap = await getDoc(casaRef);
            if (casaSnap.exists()) {
                const data = casaSnap.data() || {};
                setSaldosCasaData({
                    id: resident.id,
                    numero: resident.numero || data.numero || '',
                    propietario: resident.nombre || data.propietario || data.nombre_residente || 'Sin residente asignado',
                    adeudo: Number(data.adeudo || 0),
                    adeudoCapital: Number(data.adeudoCapital || 0),
                    adeudoIntereses: Number(data.adeudoIntereses ?? data.adeudoInteres ?? 0),
                    saldoFavor: Number(data.saldoFavor || 0)
                });
                setShowSaldosModal(true);
            } else {
                Swal.fire({
                    title: 'Aviso',
                    text: 'No se pudo encontrar el registro de esta casa en la base de datos.',
                    icon: 'warning',
                    confirmButtonColor: '#e2e8f0',
                    confirmButtonText: 'Aceptar'
                });
            }
        } catch (error) {
            console.error('Error al obtener datos de la casa para edición de saldos:', error);
            Swal.fire({
                title: 'Error de Red',
                text: 'No se pudo conectar con el servidor para leer la información.',
                icon: 'error',
                confirmButtonColor: '#ef4444',
                confirmButtonText: 'Cerrar'
            });
        }
    };



    const isResidentMoroso = useCallback((resident) => {
        return esCasaMorosa(resident, cuotaMensual, mesesPermitidos);
    }, [cuotaMensual, mesesPermitidos]);

    const normalizedSearchTerm = searchTerm.trim().toLowerCase();

    const residentsByStatus = useMemo(() => {
        const alCorriente = [];
        const morosos = [];

        /*
        console.log('🏠 DepartamentosResidentes - Clasificando residentes:', {
            totalResidentes: residents.length,
            residentes: residents.map(r => ({
                numero: r.numero,
                nombre: r.nombre_residente,
                adeudo: r.adeudo,
                saldoFavor: r.saldoFavor,
                mesesAdeudo: r.mesesAdeudo,
                rol: r.rol || r.role || r.perfil || r.profile,
            }))
        });
        */

        residents.forEach((resident) => {
            if (isCasetaProfile(resident)) {
                return; // excluir caseta/guardia/security de conteos
            }

            const adeudoAmount = Number(resident.adeudo ?? resident.deuda ?? resident.montoAdeudo ?? 0);
            const saldoFavorAmount = Number(resident.saldoFavor ?? 0);
            const mesesAdeudo = Number(resident.mesesAdeudo ?? resident.meses_deuda ?? resident.meses ?? 0);

            const isMoroso = isResidentMoroso(resident);
            const isAlCorriente = !isMoroso; // por regla, si no es moroso, cae en al corriente

            /*
            console.log(`👤 ${resident.numero} - ${resident.nombre_residente}:`, {
                adeudo: adeudoAmount,
                saldoFavor: saldoFavorAmount,
                mesesAdeudo,
                rol: resident.rol || resident.role || resident.perfil || resident.profile,
                isMoroso,
                isAlCorriente,
                clasificacion: isMoroso ? 'MOROSO' : 'AL CORRIENTE'
            });
            */

            if (isMoroso) {
                morosos.push(resident);
            } else {
                alCorriente.push(resident);
            }
        });

        /*
        console.log('📊 Resultado de clasificación:', {
            alCorriente: alCorriente.length,
            morosos: morosos.length
        });
        */

        return { alCorriente, morosos };
    }, [residents, isCasetaProfile, isResidentMoroso]);

    const filteredResidents = useMemo(() => {
        if (!normalizedSearchTerm) {
            return [];
        }

        return residents.filter((resident) => {
            const candidates = [
                resident.numero,
                resident.numeroCasa,
                resident.lote,
                resident.numero ? `casa ${resident.numero}` : '',
                resident.numeroCasa ? `casa ${resident.numeroCasa}` : '',
                resident.nombre_residente,
                resident.nombre,
                resident.nombreTitular,
                resident.titular,
                resident.propietario,
                resident.arrendador,
                resident.nombrePropietario,
                resident.nombreArrendatario,
                resident.nombreCompleto,
                resident.email,
                resident.correo,
                resident.correoElectronico,
            ];

            return candidates.some((candidate) => (
                typeof candidate === 'string' && candidate.toLowerCase().includes(normalizedSearchTerm)
            ));
        });
    }, [normalizedSearchTerm, residents]);

    const limitedResidents = useMemo(() => (
        filteredResidents.slice(0, MAX_RESULTS)
    ), [filteredResidents]);

    const hasMoreResults = filteredResidents.length > MAX_RESULTS;

    const currencyFormatter = useMemo(() => (
        new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' })
    ), []);

    const handleSearchSubmit = useCallback((event) => {
        event.preventDefault();
    }, []);

    const shouldShowResults = normalizedSearchTerm.length > 0;

    const buildCurrency = useMemo(() => (
        new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' })
    ), []);

    const handleDownloadSaldos = useCallback(async () => {
        if (!fraccionamientoId) return;
        try {
            Swal.fire({
                title: 'Generando reporte financiero...',
                text: 'Extrayendo saldos vigentes, cuotas extraordinarias y balance general.',
                allowOutsideClick: false,
                didOpen: () => {
                    Swal.showLoading();
                }
            });

            await exportarSaldosCompletosExcel({ fraccionamientoId, db });

            Swal.fire({
                icon: 'success',
                title: 'Descarga Completa',
                text: 'El libro de saldos y finanzas se ha descargado correctamente con sus 3 hojas especializadas.',
                timer: 2500,
                showConfirmButton: false
            });
        } catch (error) {
            console.error('DepartamentosResidentes: error al generar saldos', error);
            Swal.fire({
                icon: 'error',
                title: 'Error al exportar',
                text: 'Ocurrió un error al generar el archivo de saldos: ' + (error.message || 'Error de conexión')
            });
        }
    }, [fraccionamientoId, db]);

    return (
        <div className="departamentos-page">
            <div className="departamentos-container">
                <header className="departamentos-header">
                    <div className="departamentos-header-icon">
                        <FaUsers />
                    </div>
                    <div>
                        <h1>Residentes</h1>
                        <p>Clasifica el padron entre quienes estan al corriente y los morosos y mantiene la data de contacto al dia.</p>
                    </div>
                    <Link to="/administracion" className="departamentos-back-link">
                        <FaChevronLeft /> Regresar a administracion
                    </Link>
                </header>

                <section className="departamentos-content">
                    <article className="departamentos-card departamentos-card-mail-anchor">
                        <div className="departamentos-card-header">
                            <h2 className="section-title"><FaUsers /> Estado de residentes</h2>
                            <div className="overlay-actions">
                                <button
                                    type="button"
                                    className="overlay-history-button"
                                    title="Ver historial"
                                    onClick={() => setShowCorreoHistory(v => !v)}
                                >
                                    Ver historial
                                </button>
                                <button
                                    type="button"
                                    className="overlay-mail-button"
                                    title="Enviar correo personalizado"
                                    onClick={() => setShowCorreoModal(true)}
                                    aria-label="Enviar correo personalizado"
                                >
                                    <FaEnvelope />
                                </button>
                            </div>
                        </div>
                        <p>Monitorea el cumplimiento de pagos y genera acciones segun el estatus del residente.</p>

                        {/* Busqueda rapida integrada y compacta */}
                        <div className="residentes-filter compact">
                            <div className="residentes-filter-input-wrapper">
                                <FaSearch className="residentes-filter-search-icon" />
                                <input
                                    className="residentes-filter-input"
                                    type="text"
                                    placeholder="Buscar por número de casa..."
                                    value={searchTerm}
                                    onChange={(event) => setSearchTerm(event.target.value)}
                                    aria-label="Buscar residentes"
                                />
                            </div>
                            <span className="residentes-filter-helper-text">
                                Filtra por el **número de casa** del residente.
                            </span>
                            <div className="residentes-results compact">
                                {!fraccionamientoId ? (
                                    <p className="residentes-results-hint">Asigna un fraccionamiento para activar el padron.</p>
                                ) : !shouldShowResults ? (
                                    null
                                ) : isLoadingData ? (
                                    <p className="residentes-results-hint">Cargando padron...</p>
                                ) : loadError ? (
                                    <p className="residentes-results-error">{loadError}</p>
                                ) : limitedResidents.length === 0 ? (
                                    <p className="residentes-results-empty">No se encontraron coincidencias para "{searchTerm.trim()}".</p>
                                ) : (
                                    <>
                                        <div className="residentes-results-list">
                                            {limitedResidents.map((resident) => {
                                                const displayNumber = pickFirstString([
                                                    resident.numero,
                                                    resident.numeroCasa,
                                                    resident.lote,
                                                ]) || 'Sin numero';
                                                const displayName = pickFirstString([
                                                    resident.nombre_residente,
                                                    resident.nombre,
                                                    resident.nombreTitular,
                                                    resident.titular,
                                                    resident.propietario,
                                                    resident.arrendador,
                                                    resident.nombrePropietario,
                                                    resident.nombreCompleto,
                                                ]);
                                                const contactPhone = pickFirstString([
                                                    resident.telefono,
                                                    resident.telefono_principal,
                                                    resident.telefonoPrincipal,
                                                    resident.telefono1,
                                                    resident.telefono2,
                                                    resident.telefonoSecundario,
                                                    resident.celular,
                                                ]);
                                                const contactEmail = pickFirstString([
                                                    resident.email,
                                                    resident.correo,
                                                    resident.correoElectronico,
                                                ]);
                                                const idNorm = String(resident.id || '').trim().toUpperCase();
                                                const numNorm = String(resident.numero || resident.numeroCasa || '').trim().toUpperCase();
                                                const adeudoAguaMap = (mapAguaPendiente[idNorm] || 0) || (mapAguaPendiente[numNorm] || 0);
                                                const adeudoAgua = adeudoAguaMap || Number(resident.adeudoAgua || resident.montoAguaPendiente || 0);
                                                const saldoFavorAmount = Number(resident.saldoFavor ?? 0);

                                                const adeudoMultas = (mapMultasPendientes[idNorm] || 0) || (mapMultasPendientes[numNorm] || 0) || Number(resident.adeudoMultas || resident.sancionesAdmin || 0);
                                                const adeudoExtras = (mapExtrasPendientes[idNorm] || 0) || (mapExtrasPendientes[numNorm] || 0) || Number(resident.cuotasExtras || resident.adeudoExtras || 0);

                                                const adeudoCapitalCasa = Number(resident.adeudoCapital ?? 0);
                                                const adeudoInteresesCasa = Number(resident.adeudoIntereses ?? resident.adeudoInteres ?? 0);
                                                const adeudoMantenimientoReal = (resident.adeudoCapital !== undefined && resident.adeudoCapital !== null)
                                                    ? (adeudoCapitalCasa + adeudoInteresesCasa)
                                                    : Number(resident.adeudo ?? resident.deuda ?? resident.montoAdeudo ?? 0);

                                                const residentParaCalculo = {
                                                    ...resident,
                                                    adeudo: adeudoMantenimientoReal,
                                                    deuda: adeudoMantenimientoReal,
                                                    montoAdeudo: adeudoMantenimientoReal
                                                };

                                                const adeudoBase = saldoFavorAmount > 0 ? 0 : calcularAdeudoVisibleDesdeObjeto(residentParaCalculo, { cuotaMensual, mesesPermitidos });
                                                const mantenimientoMonto = adeudoBase;

                                                const sumaAdeudosAdmin = mantenimientoMonto + adeudoAgua + adeudoMultas + adeudoExtras;
                                                const totalRealCobro = Math.max(0, Math.round((sumaAdeudosAdmin - saldoFavorAmount) * 100) / 100);
                                                const isMoroso = isResidentMoroso(resident);

                                                return (
                                                    <div key={resident.id || displayNumber} className="residente-result">
                                                        {/* Badge de estado absoluto en la esquina superior derecha */}
                                                        <span className={`residente-status-badge residente-pill ${isMoroso ? 'moroso' : 'corriente'}`}>
                                                            {isMoroso ? 'Con adeudo' : 'Al corriente'}
                                                        </span>

                                                        <div className="residente-top-row">
                                                            <div className="residente-main" style={{ paddingRight: '120px' }}>
                                                                <span className="residente-number">Casa {displayNumber}</span>
                                                                <span className={`residente-name${displayName ? '' : ' muted'}`}>
                                                                    {displayName || 'Nombre no registrado'}
                                                                </span>
                                                                {(contactPhone || contactEmail) && (
                                                                    <div className="residente-contact">
                                                                        {contactPhone && <span>Tel. {contactPhone}</span>}
                                                                        {contactEmail && <span>{contactEmail}</span>}
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <div className="residente-meta">
                                                                {mantenimientoMonto > 0 && (
                                                                    <span className="residente-pill adeudo" title="Adeudo pendiente de cuotas ordinarias de mantenimiento">
                                                                        Mantenimiento: {currencyFormatter.format(mantenimientoMonto)}
                                                                    </span>
                                                                )}
                                                                {adeudoMultas > 0 && (
                                                                    <span className="residente-pill adeudo" style={{ backgroundColor: '#fce7f3', color: '#db2777', borderColor: '#db2777' }} title="Sanciones administrativas pendientes">
                                                                        Sanciones: {currencyFormatter.format(adeudoMultas)}
                                                                    </span>
                                                                )}
                                                                {adeudoExtras > 0 && (
                                                                    <span className="residente-pill adeudo" style={{ backgroundColor: '#fef3c7', color: '#d97706', borderColor: '#d97706' }} title="Cuotas extraordinarias pendientes">
                                                                        Extras: {currencyFormatter.format(adeudoExtras)}
                                                                    </span>
                                                                )}
                                                                {adeudoAgua > 0 && (
                                                                    <span className="residente-pill moroso" style={{ backgroundColor: '#e0f2fe', color: '#0284c7', borderColor: '#0284c7' }} title="Adeudo pendiente por Consumo de Agua Potable">
                                                                        <FaTint /> Agua: {currencyFormatter.format(adeudoAgua)}
                                                                    </span>
                                                                )}
                                                                {saldoFavorAmount > 0 && (
                                                                    <span className="residente-pill saldo-favor">
                                                                        Saldo a favor: {currencyFormatter.format(saldoFavorAmount)}
                                                                    </span>
                                                                )}
                                                                {totalRealCobro > 0 && (
                                                                    <span className="residente-pill adeudo-total" title="Suma total de deudas (mantenimiento + sanciones + extras + agua)">
                                                                        Adeudo Total: {currencyFormatter.format(totalRealCobro)}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div className="residente-bottom-row">
                                                            <div className="residente-actions">
                                                                {(() => {
                                                                    const esCaseta = esCasaCaseta(resident);

                                                                    if (esCaseta) {
                                                                        return (
                                                                            <button
                                                                                type="button"
                                                                                className="btn-residente-action action-profile"
                                                                                title="Ver y editar datos de contacto de caseta"
                                                                                onClick={() => setSelectedResidentForProfile(resident)}
                                                                            >
                                                                                <FaUserCheck /> Perfil
                                                                            </button>
                                                                        );
                                                                    }

                                                                    return (
                                                                        <>
                                                                            <button
                                                                                type="button"
                                                                                className="btn-residente-action action-profile"
                                                                                title="Ver tarjeta de contacto detallada"
                                                                                onClick={() => {
                                                                                    navigate(`/administracion/departamentos/residentes/${isMoroso ? 'morosos' : 'al-corriente'}`, {
                                                                                        state: {
                                                                                            search: `Casa ${displayNumber}`,
                                                                                            originalSearch: searchTerm
                                                                                        }
                                                                                    });
                                                                                }}
                                                                            >
                                                                                <FaUserCheck /> Ir/Ver
                                                                            </button>
                                                                            <button
                                                                                type="button"
                                                                                className="btn-residente-action action-history"
                                                                                title="Ver historial de ingresos"
                                                                                onClick={() => {
                                                                                    navigate(`/administracion/departamentos/residentes/${isMoroso ? 'morosos' : 'al-corriente'}`, {
                                                                                        state: {
                                                                                            search: `Casa ${displayNumber}`,
                                                                                            selectedResidentId: resident.id,
                                                                                            openHistory: true,
                                                                                            originalSearch: searchTerm
                                                                                        }
                                                                                    });
                                                                                }}
                                                                            >
                                                                                <FaClipboardList /> Historial
                                                                            </button>
                                                                            <button
                                                                                type="button"
                                                                                className="btn-residente-action action-ticket"
                                                                                title="Registrar pago / Generar ticket"
                                                                                onClick={() => {
                                                                                    navigate('/admin/tickets', {
                                                                                        state: {
                                                                                            search: `Casa ${displayNumber}`,
                                                                                            selectedCasaId: resident.id
                                                                                        }
                                                                                    });
                                                                                }}
                                                                            >
                                                                                <FaWallet /> Generar Ticket/Recibo
                                                                            </button>
                                                                            {(userData?.rol === 'superadmin' || userData?.rol === 'platformadmin' || userData?.rol === 'admin') && (
                                                                                <button
                                                                                    type="button"
                                                                                    className="btn-residente-action action-saldos-shortcut"
                                                                                    title="Editar Saldos / Adeudos"
                                                                                    onClick={() => handleOpenEditSaldosModal(resident)}
                                                                                >
                                                                                    💲
                                                                                </button>
                                                                            )}
                                                                        </>
                                                                    );
                                                                })()}
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                        {hasMoreResults && (
                                            <p className="residentes-results-hint">
                                                Se muestran las primeras {limitedResidents.length} coincidencias. Refina tu busqueda para ver mas resultados.
                                            </p>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>

                        <div className="status-grid">
                            <div className="status-card ok">
                                <div className="status-card-header">
                                    <h3><FaUserCheck /> Al corriente</h3>
                                    <span className="status-count">{residentsByStatus.alCorriente.length}</span>
                                </div>
                                <p>Pagos al dia e informacion de contacto verificada.</p>
                                <div className="status-card-actions">
                                    <button
                                        type="button"
                                        className="status-button"
                                        onClick={() => navigate('/administracion/departamentos/residentes/al-corriente')}
                                        disabled={residentsByStatus.alCorriente.length === 0}
                                    >
                                        Ver residentes
                                    </button>
                                </div>
                            </div>
                            <div className="status-card alert">
                                <div className="status-card-header">
                                    <h3><FaUserTimes /> Morosos</h3>
                                    <span className="status-count">{residentsByStatus.morosos.length}</span>
                                </div>
                                <p>Restringe accesos automaticos y genera recordatorios personalizados.</p>
                                <div className="status-card-actions">
                                    <button
                                        type="button"
                                        className="status-button danger"
                                        onClick={() => navigate('/administracion/departamentos/residentes/morosos')}
                                        disabled={residentsByStatus.morosos.length === 0}
                                    >
                                        Gestionar morosos
                                    </button>
                                </div>
                            </div>
                        </div>
                    </article>
                    {/* Saldos Actuales debajo de tarjetas */}
                    <article className="departamentos-card">
                        <div className="departamentos-card-header">
                            <h2 className="section-title"><FaWallet /> Saldos actuales</h2>
                        </div>
                        <p>Descarga los saldos vigentes por vivienda con formato de moneda y encabezado del fraccionamiento.</p>
                        <div className="owner-grid" style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                            <button type="button" className="primary-button" onClick={handleDownloadSaldos}>
                                <FaDownload /> Descargar saldos
                            </button>
                            <button
                                type="button"
                                className="primary-button"
                                style={{ backgroundColor: '#4f46e5' }}
                                onClick={() => setShowImportarTagsModal(true)}
                            >
                                <FaIdCard /> Importar tarjetas RFID Tag's
                            </button>
                        </div>
                    </article>
                    {showCorreoModal && (
                        <EnviarCorreoModal
                            isOpen={showCorreoModal}
                            onClose={() => setShowCorreoModal(false)}
                        />
                    )}
                    {showCorreoHistory && (
                        <HistorialBoletinesPanel
                            isOpen={showCorreoHistory}
                            onClose={() => setShowCorreoHistory(false)}
                            onResend={(item) => {
                                // Abrir modal con datos del historial
                                setShowCorreoModal(true);
                                setShowCorreoHistory(false);
                            }}
                        />
                    )}

                    {selectedResidentForProfile && (
                        <ResidentProfileModal
                            open={true}
                            resident={selectedResidentForProfile}
                            onClose={() => setSelectedResidentForProfile(null)}
                        />
                    )}

                    {showSaldosModal && saldosCasaData && (
                        <ModalEdicionSaldos
                            isOpen={showSaldosModal}
                            casa={saldosCasaData}
                            fraccionamientoId={fraccionamientoId}
                            userData={userData}
                            onClose={() => setShowSaldosModal(false)}
                            onSuccess={() => setShowSaldosModal(false)}
                        />
                    )}

                    {showImportarTagsModal && (
                        <ModalImportarTagsRFID
                            isOpen={showImportarTagsModal}
                            onClose={() => setShowImportarTagsModal(false)}
                            fraccionamientoId={fraccionamientoId}
                            casas={residents}
                        />
                    )}

                    {/* Sección 'Herramientas del padrón' eliminada por requerimiento */}
                </section>
            </div>
        </div>
    );
};

export default DepartamentosResidentes;
